/**
 * Chat Availability Service
 *
 * Manages chat functionality availability based on user authentication
 * and subscription status. By default, chat is disabled until user
 * signs in and has an active subscription.
 */

import { createLogger } from '@ctrl-enter/tools/utils';
import React, { createContext, useContext, useMemo } from 'react';
import { getChatConfig } from '../config/chat';
import { isDevelopment } from '../env';
import { useCheckChatAccessQuery } from '../hooks/use-gateway-queries';
import { useSessionStore } from '../stores/session-store';
import { toast } from '../utils/toast';
import { useAuth } from './auth-service';
import { useElectron } from './electron/electron-provider';

const logger = createLogger('client:service:chat-availability-service');

export type ChatAvailabilityStatus =
  | 'available' // User is authenticated and has active subscription
  | 'requires_auth' // User needs to sign in
  | 'requires_agreement' // Admin needs to sign PandaDoc agreement before payment
  | 'requires_payment' // User is authenticated but needs to add payment/subscription
  | 'billing_suspended' // Manual (invoice) org paused by Enter staff — no self-serve payment
  | 'insufficient_seats' // Admin user with subscription but not enough seats for members
  | 'not_a_member' // User is authenticated but not a member of organization
  | 'loading'; // Auth state is still loading

export interface ChatAvailabilityState {
  status: ChatAvailabilityStatus;
  isEnabled: boolean;
  message: string;
  actionLabel?: string;
  onAction?: () => Promise<void> | void;
  onRefresh?: () => Promise<void> | void;
  requirements?: string[]; // Array of requirements from backend (e.g., ['requires_agreement', 'requires_payment'])
}

interface ChatAvailabilityContextType {
  availability: ChatAvailabilityState;
  refreshAvailability: () => void;
}

const ChatAvailabilityContext = createContext<ChatAvailabilityContextType | null>(null);

interface ChatAvailabilityProviderProps {
  children: React.ReactNode;
  // Allow override for testing or special cases
  forceEnabled?: boolean;
  // Navigation callback for billing/payment setup
  onNavigateToBilling?: () => void;
}

export const ChatAvailabilityProvider: React.FC<ChatAvailabilityProviderProps> = ({
  children,
  forceEnabled = false,
  onNavigateToBilling,
}) => {
  logger.info('🟢 [CHAT-AVAILABILITY] Provider rendering');

  const { isAuthenticated, isLoading, login, user, getAccessToken } = useAuth();
  const hasSignedAgreement = useSessionStore(state => state.hasSignedAgreement);
  const setHasSignedAgreement = useSessionStore(state => state.setHasSignedAgreement);
  const { billing } = useElectron();

  React.useEffect(() => {
    logger.info('🟢 [CHAT-AVAILABILITY] Provider mounted');
    return () => {
      logger.info('🔴 [CHAT-AVAILABILITY] Provider unmounting');
    };
  }, []);

  // Check if user is admin and get organization data accordingly
  const isUserOrganizationAdmin = user?.role === 'admin';

  // Check organization-level chat access (for both admins and members)
  const {
    data: chatAccessData,
    isLoading: isChatAccessLoading,
    error: chatAccessError,
    refetch: refetchChatAccess,
  } = useCheckChatAccessQuery(isAuthenticated && !!user?.organizationId);

  // Auto-link customer functionality has been removed
  // There should only be one stripe customer per WorkOS organization that is created on WorkOS organization creation

  // Organization linking is now handled automatically during login
  // No need to call it separately when accessing chat

  // Cache configuration outside of useMemo to avoid repeated calls
  const config = React.useMemo(() => getChatConfig(), []);

  // Memoize login action to prevent unnecessary re-renders
  const handleLogin = React.useCallback(async () => {
    await login();
  }, [login]);

  // Agreement handling is now done by the AgreementPrompt component directly
  // No action needed here - just show the prompt
  const handleAgreement = React.useCallback(() => {
    // No-op - AgreementPrompt handles the signing flow
  }, []);

  // Refresh is handled by re-checking the chat access status
  const handleRefreshAgreement = React.useCallback(() => {
    // The AgreementPrompt component handles status checking
    // This just refreshes the page for now
    window.location.reload();
  }, []);

  // Memoize payment action to prevent unnecessary re-renders
  const handlePayment = React.useCallback(async () => {
    if (!user?.id) {
      logger.error('ChatAvailability: No user ID available for billing');
      return;
    }

    logger.info('ChatAvailability: Opening billing/pricing interface for user:', user.id);

    // Check if user has subscription via entitlements in chat access data
    const hasSubscriptionAccess = chatAccessData?.body?.hasAccess;

    // DEBUG: Log all the values we're checking
    logger.info('ChatAvailability: DEBUG - Decision values:', {
      hasSubscriptionAccess,
      userRole: user.role,
      isAdmin: user.role === 'admin',
      chatAccessData: chatAccessData,
      chatAccessBody: chatAccessData?.body,
      willOpenCustomerPortal: hasSubscriptionAccess && user.role === 'admin',
    });

    if (hasSubscriptionAccess && user.role === 'admin') {
      // User has subscription entitlement and is admin - open customer portal to manage
      logger.info('ChatAvailability: User has subscription entitlement, opening customer portal');
      billing.openCustomerPortal();
    } else {
      // User needs subscription - navigate to billing panel
      // The billing panel will show the embedded pricing table
      logger.info('ChatAvailability: User needs subscription, navigating to billing panel');

      if (onNavigateToBilling) {
        onNavigateToBilling();
      } else {
        // Fallback: show a toast if no navigation callback is provided
        toast.error('Subscription Required', 'Please visit the Billing section to subscribe.');
      }
    }
  }, [user?.id, user?.role, chatAccessData?.body?.hasAccess, billing, onNavigateToBilling]);

  // Stabilize error status to prevent re-renders on every error object change
  const errorStatus = React.useMemo(() => {
    if (!chatAccessError) return null;

    // Extract stable error information
    const status =
      typeof chatAccessError === 'object' && 'status' in chatAccessError
        ? (chatAccessError.status as number)
        : null;

    const errorType =
      chatAccessError &&
      typeof chatAccessError === 'object' &&
      'body' in chatAccessError &&
      chatAccessError.body &&
      typeof chatAccessError.body === 'object' &&
      'error' in chatAccessError.body
        ? String(chatAccessError.body.error)
        : 'unknown';

    return { status, errorType };
  }, [chatAccessError]);

  const hasSessionError = errorStatus?.errorType === 'session_invalid';

  const availability = useMemo((): ChatAvailabilityState => {
    logger.info('🔄 [CHAT-AVAILABILITY] Recalculating availability state');

    // Force enabled override (for testing or admin users)
    if (forceEnabled || (config.development?.forceEnabled && isDevelopment)) {
      logger.warn('⚠️ [CHAT-AVAILABILITY] Force enabled override active!', {
        forceEnabled,
        devForceEnabled: config.development?.forceEnabled,
        isDevelopment,
      });
      return {
        status: 'available',
        isEnabled: true,
        message: 'Chat is available (force enabled)',
      };
    }

    // Loading state - check auth and chat access loading
    if (isLoading || (isAuthenticated && isChatAccessLoading)) {
      return {
        status: 'loading',
        isEnabled: false,
        message: 'Loading...',
      };
    }

    // Check authentication requirement
    if (config.requireAuth && !isAuthenticated) {
      return {
        status: 'requires_auth',
        isEnabled: false,
        message: 'You need to sign in to use the chat functionality',
        actionLabel: 'Sign In',
        onAction: handleLogin,
      };
    }

    // If user is authenticated, check if they are a member of an organization
    if (isAuthenticated && !user?.organizationId) {
      return {
        status: 'not_a_member',
        isEnabled: false,
        message: 'Chat is not available. Please contact your administrator',
      };
    }

    // Check if chat access query failed (could be due to session invalidation)
    // ⚠️ IMPORTANT: User IS authenticated at this point (checked above)
    // Don't redirect to sign-in - it causes infinite loops!
    if (errorStatus) {
      logger.warn(
        '⚠️ [CHAT-AVAILABILITY] User authenticated but chat access check failed',
        errorStatus
      );

      // If session error, user is still authenticated - allow access with warning
      // Backend will handle token refresh, don't block authenticated users
      if (hasSessionError) {
        logger.warn('⚠️ [CHAT-AVAILABILITY] Session error but user authenticated, allowing access');
        return {
          status: 'available',
          isEnabled: true,
          message: 'Chat is available (session syncing...)',
        };
      }

      // SECURITY FIX: Other API errors should NOT grant access
      // Fail-closed: If we can't verify access, assume NO access and prompt for payment
      // This prevents new users from getting free chat access when API errors
      logger.warn('⚠️ [CHAT-AVAILABILITY] API error - assuming no access, prompting for payment');
      return {
        status: 'requires_payment',
        isEnabled: false,
        message: 'Unable to verify subscription. Please subscribe to use chat.',
        actionLabel: 'Subscribe',
        onAction: handlePayment,
      };
    }

    // Use entitlement-based chat access check - this is the authoritative source
    const accessResult = chatAccessData?.body;
    const requirements = accessResult?.requirements ? accessResult?.requirements : [];

    // DEBUG: Log the actual data we received
    logger.info('🔍 [CHAT-AVAILABILITY] Access check data:', {
      hasAccessResult: !!accessResult,
      hasAccess: accessResult?.hasAccess,
      requirements: requirements,
      requireSubscriptionConfig: config.requireSubscription,
      isAuthenticated: isAuthenticated,
      willCheckPayment:
        isAuthenticated &&
        config.requireSubscription &&
        requirements.indexOf('requires_payment') !== -1,
    });

    // Safety check - if accessResult is undefined, treat as no access
    if (!accessResult) {
      logger.warn('⚠️ [CHAT-AVAILABILITY] No accessResult - denying access');
      return {
        status: 'requires_auth',
        isEnabled: false,
        message: 'Unable to check chat access. Please try again.',
        actionLabel: 'Sign In',
        onAction: handleLogin,
      };
    }

    // Manual (invoice) org paused by Enter staff — show a "paused" notice, never a
    // Stripe Subscribe prompt (they can't self-serve payment).
    if (isAuthenticated && requirements.indexOf('billing_suspended') != -1) {
      logger.info('⏸️ [CHAT-AVAILABILITY] Billing suspended - showing paused notice');
      return {
        status: 'billing_suspended',
        isEnabled: false,
        message:
          'Your account is paused. Please contact your Enter representative to restore access.',
        requirements,
      };
    }

    if (
      isAuthenticated &&
      config.requireSubscription &&
      requirements.indexOf('requires_payment') != -1
    ) {
      logger.info('✅ [CHAT-AVAILABILITY] Payment required - showing subscription prompt');
      return {
        status: 'requires_payment',
        isEnabled: false,
        message: 'You need a subscription to use the chat functionality',
        actionLabel: 'Subscribe',
        onAction: handlePayment,
        requirements: requirements, // Include full requirements array
      };
    }

    if (isAuthenticated && requirements.indexOf('requires_agreement') != -1) {
      return {
        status: 'requires_agreement',
        isEnabled: false,
        message: 'Please sign the service agreement before setting up your subscription',
        actionLabel: 'Sign Agreement',
        onAction: handleAgreement,
        onRefresh: handleRefreshAgreement,
        requirements: requirements, // Include full requirements array
      };
    }

    // If user has access via entitlements, enable chat for everyone
    if (accessResult.hasAccess) {
      logger.info('✅ [CHAT-AVAILABILITY] User has access - enabling chat');
      return {
        status: 'available',
        isEnabled: true,
        message: 'Chat is available',
      };
    } else {
      // Fallback - if no requirements specified but no access, prompt for payment
      logger.warn(
        '⚠️ [CHAT-AVAILABILITY] No access and no requirements - defaulting to payment required'
      );
      return {
        status: 'requires_payment',
        isEnabled: false,
        message: 'You need a subscription to use chat.',
        actionLabel: 'Subscribe',
        onAction: handlePayment,
      };
    }
  }, [
    isAuthenticated,
    isLoading,
    hasSignedAgreement,
    forceEnabled,
    config,
    handleLogin,
    handleAgreement,
    handleRefreshAgreement,
    handlePayment,
    user?.organizationId,
    isUserOrganizationAdmin,
    chatAccessData,
    errorStatus, // ← Use stable errorStatus instead of chatAccessError object
    isChatAccessLoading,
    hasSessionError,
  ]);

  const refreshAvailability = React.useCallback(() => {
    logger.info('🔄 [CHAT-AVAILABILITY] Manually refreshing chat access status');
    // Refetch chat access to get latest entitlements
    refetchChatAccess();
  }, [refetchChatAccess]);

  const contextValue: ChatAvailabilityContextType = React.useMemo(
    () => ({
      availability,
      refreshAvailability,
    }),
    [availability, refreshAvailability]
  );

  return (
    <ChatAvailabilityContext.Provider value={contextValue}>
      {children}
    </ChatAvailabilityContext.Provider>
  );
};

/**
 * Hook to access chat availability state
 */
export const useChatAvailability = (): ChatAvailabilityContextType => {
  const context = useContext(ChatAvailabilityContext);
  if (!context) {
    throw new Error('useChatAvailability must be used within a ChatAvailabilityProvider');
  }
  return context;
};

/**
 * Convenience hook to check if chat is enabled
 */
export const useIsChatEnabled = (): boolean => {
  const { availability } = useChatAvailability();
  return availability.isEnabled;
};

/**
 * Convenience hook to get chat status
 */
export const useChatStatus = (): ChatAvailabilityStatus => {
  const { availability } = useChatAvailability();
  return availability.status;
};
